import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Badge, Toggle } from '@teals/ui-web';
import type { Agent } from '../../store/api';
import type { ConfigSection, ConfigChatMessage, CapabilityToggle as CapToggle } from './configureTypes';
import {
  mockDocuments,
  mockKnowledgeSources,
  mockTasks,
  mockTools,
  mockFleetConnections,
  mockCapabilities,
  mockConversationStyle,
  mockSpecialistPrompts,
  mockOutputFormat,
  suggestionChips,
  buildMockChatMessages,
} from './configureMocks';

/* ===================================================================
   LAYOUT
   =================================================================== */

const TwoPanel = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const PanelLeft = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--border);

  @media (max-width: 1024px) {
    border-right: none;
    border-bottom: 1px solid var(--border);
  }
`;

const PanelRight = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-primary);
`;

const PanelHeader = styled.div`
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-surface);
  flex-shrink: 0;
`;

const PanelScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/* ===================================================================
   LEFT PANEL — AI CONFIGURATION ASSISTANT
   =================================================================== */

const EditorMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const EditorMsg = styled.div<{ $role: 'ai' | 'user' }>`
  display: flex;
  gap: 0.75rem;
  ${({ $role }) => ($role === 'user' ? 'flex-direction: row-reverse;' : '')}
`;

const MsgAvatar = styled.div<{ $role: 'ai' | 'user' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $role }) => ($role === 'ai' ? 'var(--accent-subtle)' : 'var(--bg-elevated)')};
  color: ${({ $role }) => ($role === 'ai' ? 'var(--accent)' : 'var(--text-secondary)')};
`;

const MsgContent = styled.div<{ $role: 'ai' | 'user' }>`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.6;
  max-width: 85%;
  white-space: pre-wrap;
  ${({ $role }) =>
    $role === 'ai'
      ? 'background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-secondary); border-top-left-radius: 4px;'
      : 'background: var(--accent); color: white; border-top-right-radius: 4px;'}
`;

const DiffBlock = styled.div`
  margin-top: 0.75rem;
  background: var(--code-bg);
  border-radius: 8px;
  overflow: hidden;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
`;

const DiffHeaderEl = styled.div`
  padding: 0.5rem 0.75rem;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const DiffLineEl = styled.div<{ $type: 'added' | 'removed' }>`
  padding: 0.25rem 0.75rem;
  white-space: pre-wrap;
  background: ${({ $type }) => ($type === 'removed' ? 'var(--error-subtle)' : 'var(--success-subtle)')};
  color: ${({ $type }) => ($type === 'removed' ? 'var(--error)' : 'var(--success)')};
`;

const DiffActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const MsgNote = styled.p`
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const EditorInputArea = styled.div`
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
`;

const EditorInputWrap = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const EditorInput = styled.textarea`
  flex: 1;
  padding: 0.75rem 1rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 120px;
  line-height: 1.5;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
  &::placeholder {
    color: var(--text-muted);
  }
`;

const ChipsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--bg-surface);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

/* ===================================================================
   RIGHT PANEL — COLLAPSIBLE SECTION CARDS
   =================================================================== */

const SectionCard = styled.div<{ $active: boolean }>`
  background: var(--bg-surface);
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.2s, border-color 0.2s;

  ${({ $active }) => $active && 'box-shadow: 0 0 0 1px var(--accent-subtle);'}

  &:hover {
    box-shadow: var(--shadow-sm);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: var(--bg-elevated);
  }
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const SectionIcon = styled.div<{ $variant: 'behavior' | 'knowledge' | 'tasks' | 'prompts' }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  background: ${({ $variant }) =>
    $variant === 'behavior'
      ? 'var(--accent-subtle)'
      : $variant === 'knowledge'
        ? 'var(--info-subtle)'
        : $variant === 'prompts'
          ? 'rgba(139, 92, 246, 0.15)'
          : 'var(--warning-subtle)'};
`;

const SectionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const SectionSummary = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const SectionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PendingBadge = styled.span`
  font-size: 0.6875rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: var(--warning-subtle);
  color: var(--warning);
  font-weight: 600;
  white-space: nowrap;
`;

const SectionActionBtn = styled.button`
  font-size: 0.6875rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  background: var(--accent-subtle);
  color: var(--accent);
  border: none;
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
  font-family: inherit;
  transition: all 0.15s;

  &:hover {
    background: var(--accent);
    color: white;
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  font-size: 0.75rem;
  color: var(--text-muted);
  transition: transform 0.2s;
  ${({ $open }) => $open && 'transform: rotate(90deg);'}
`;

const SectionBody = styled.div`
  padding: 0 1.25rem 1.25rem;
  border-top: 1px solid var(--border);
`;

const SubsectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-top: 1.25rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

/* -- Config rows -- */

const ConfigCard = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.875rem 1rem;
  margin-top: 0.75rem;
`;

const ConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-child {
    border-bottom: none;
  }
`;

const ConfigKey = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const ConfigValue = styled.span<{ $changed?: boolean }>`
  color: var(--text-primary);
  font-weight: 500;
  ${({ $changed }) => $changed && 'color: var(--accent); font-weight: 600;'}
`;

const PromptPreview = styled.pre`
  background: var(--code-bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 1rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
  margin-top: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
`;

const PromptFullPreview = styled.pre`
  background: var(--code-bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 1rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
  margin-top: 0.75rem;
  min-height: 120px;
  max-height: 400px;
  overflow-y: auto;
`;

const SpecialistPromptCard = styled.div<{ $open: boolean }>`
  margin-top: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
`;

const SpecialistPromptHeader = styled.div`
  padding: 0.625rem 0.875rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-elevated);

  &:hover {
    background: var(--bg-surface);
  }
`;

const SpecialistPromptBody = styled.pre`
  padding: 0.875rem 1rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--code-bg);
  border-top: 1px solid var(--border);
  margin: 0;
`;

const SchemaFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  font-size: 0.8125rem;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const SchemaFieldName = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: var(--text-primary);
  min-width: 120px;
`;

const SchemaFieldType = styled(Badge)`
  font-size: 0.625rem;
`;

const SpecialistPromptCollapsible: React.FC<{
  agentName: string;
  prompt: string;
}> = ({ agentName, prompt }) => {
  const [open, setOpen] = useState(false);
  return (
    <SpecialistPromptCard $open={open}>
      <SpecialistPromptHeader onClick={() => setOpen((o) => !o)}>
        {agentName}
        <Chevron $open={open}>{'\u25B6'}</Chevron>
      </SpecialistPromptHeader>
      {open && <SpecialistPromptBody>{prompt}</SpecialistPromptBody>}
    </SpecialistPromptCard>
  );
};

const StyleBadges = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

/* -- Knowledge section -- */

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-child {
    border-bottom: none;
  }
`;

const FileIcon = styled.div`
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const UploadZone = styled.div`
  margin-top: 0.75rem;
  padding: 1.25rem;
  border: 2px dashed var(--border);
  border-radius: 10px;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const UploadZoneHint = styled.div`
  font-size: 0.6875rem;
  margin-top: 0.25rem;
  color: var(--text-muted);
`;

const SourceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--bg-primary);
  border: 1px solid var(--border);
  border-radius: 10px;
  margin-top: 0.75rem;
`;

const SourceIcon = styled.div<{ $bg: string; $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const SourceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SourceName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const SourceMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const StatusDot = styled.div<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) =>
    $status === 'connected' ? 'var(--success)' : $status === 'syncing' ? 'var(--warning)' : 'var(--error)'};
`;

const IntegrationLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.625rem;
  border: 1px dashed var(--accent);
  border-radius: 8px;
  background: transparent;
  color: var(--accent);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-subtle);
  }
`;

/* -- Tasks & Tools section -- */

const TaskItem = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const TaskIconWrap = styled.div`
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const TaskInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const TaskDesc = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const SchemaPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.375rem;
`;

const SchemaPill = styled.span`
  font-size: 0.6875rem;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: var(--bg-elevated);
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
  white-space: nowrap;
`;

const CapabilityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const CapabilityLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 500;
`;

const FleetMember = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const FleetIcon = styled.div<{ $color: 'info' | 'warning' }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 700;
  flex-shrink: 0;
  background: ${({ $color }) => ($color === 'info' ? 'var(--info-subtle)' : 'var(--warning-subtle)')};
  color: ${({ $color }) => ($color === 'info' ? 'var(--info)' : 'var(--warning)')};
`;

const FleetName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const FleetDesc = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const DashedBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.5rem;
  border: 1px dashed var(--border);
  border-radius: 8px;
  background: transparent;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

/* -- Version bar -- */

const VersionBarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const VersionLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const VersionBadge = styled.span<{ $variant: 'draft' | 'sandbox' | 'published' }>`
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  ${({ $variant }) =>
    $variant === 'draft'
      ? 'background: var(--bg-elevated); color: var(--text-muted);'
      : $variant === 'sandbox'
        ? 'background: var(--warning-subtle); color: var(--warning);'
        : 'background: var(--success-subtle); color: var(--success);'}
`;

const VersionArrow = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const PendingDot = styled.span`
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--success);
`;

/* ===================================================================
   FILE-STATUS BADGE HELPERS
   =================================================================== */

const FILE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error'> = {
  indexed: 'success',
  processing: 'warning',
  failed: 'error',
};

/* ===================================================================
   COMPONENT
   =================================================================== */

interface ConfigureTabProps {
  agent: Agent;
  onSave?: () => void;
  isSaving?: boolean;
}

export const ConfigureTab: React.FC<ConfigureTabProps> = ({ agent }) => {
  const [activeSection, setActiveSection] = useState<ConfigSection | null>('behavior');
  const [capabilities, setCapabilities] = useState<CapToggle[]>(mockCapabilities);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const agentName = agent.displayName ?? agent.agentName;

  const chatMessages = useMemo<ConfigChatMessage[]>(
    () => buildMockChatMessages(agentName),
    [agentName],
  );

  const currentChips = useMemo(
    () => suggestionChips[activeSection ?? 'behavior'],
    [activeSection],
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const toggleSection = (section: ConfigSection) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const toggleCapability = (id: string) => {
    setCapabilities((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c)),
    );
  };

  const versions = agent.config?.versions;
  const draftVersion = versions?.find((v) => v.status === 'draft');
  const sandboxVersion = versions?.find((v) => v.status === 'sandbox');
  const publishedVersion = versions?.find((v) => v.status === 'published');

  const pendingCount = 3; // mock

  return (
    <TwoPanel data-testid="configure-tab">
      {/* =================== LEFT PANEL =================== */}
      <PanelLeft>
        <PanelHeader>
          <span style={{ color: 'var(--accent)' }}>{'\u2728'}</span>
          AI Configuration Assistant
        </PanelHeader>

        <EditorMessages>
          {chatMessages.map((msg) => (
            <EditorMsg key={msg.id} $role={msg.role}>
              <MsgAvatar $role={msg.role}>
                {msg.role === 'ai' ? 'AI' : agentName.slice(0, 2).toUpperCase()}
              </MsgAvatar>
              <MsgContent $role={msg.role}>
                {msg.content}
                {msg.diffs?.map((diff, di) => (
                  <DiffBlock key={di}>
                    <DiffHeaderEl>{diff.header}</DiffHeaderEl>
                    {diff.lines.map((line, li) => (
                      <DiffLineEl key={li} $type={line.type}>
                        {line.text}
                      </DiffLineEl>
                    ))}
                  </DiffBlock>
                ))}
                {msg.diffs && msg.diffs.length > 0 && (
                  <>
                    {msg.diffs.length === 1 && msg.id === 'msg-3' && (
                      <MsgNote>
                        {agentName} will index 23 pages from the Menu Documentation and Wine List
                        spaces. Sync set to daily.
                      </MsgNote>
                    )}
                    <DiffActions>
                      <Button variant="primary" size="sm">
                        {'\u2713'} {msg.diffs.length > 1 ? 'Apply Both' : 'Apply Change'}
                      </Button>
                      <Button variant="ghost" size="sm">
                        {'\u2717'} Discard
                      </Button>
                    </DiffActions>
                  </>
                )}
              </MsgContent>
            </EditorMsg>
          ))}
          <div ref={messagesEndRef} />
        </EditorMessages>

        <EditorInputArea>
          <EditorInputWrap>
            <EditorInput
              rows={1}
              placeholder='Describe a change... "Upload our menu PDF as knowledge"'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <Button
              variant="primary"
              style={{ width: 40, height: 40, borderRadius: 10, padding: 0 }}
              disabled={!inputValue.trim()}
            >
              {'\u2191'}
            </Button>
          </EditorInputWrap>
          <ChipsRow>
            {currentChips.map((chip) => (
              <Chip key={chip.label}>
                {chip.icon} {chip.label}
              </Chip>
            ))}
            <Chip>{'\u{1F504}'} Reset Changes</Chip>
          </ChipsRow>
        </EditorInputArea>
      </PanelLeft>

      {/* =================== RIGHT PANEL =================== */}
      <PanelRight>
        <PanelHeader>
          <span>{'\u2699'}</span> Configuration
          <PendingDot style={{ marginLeft: 'auto' }}>
            {'\u25CF'} {pendingCount} pending changes
          </PendingDot>
        </PanelHeader>

        <PanelScroll>
          {/* ===== SECTION 1: BEHAVIOR ===== */}
          <SectionCard $active={activeSection === 'behavior'}>
            <SectionHeader onClick={() => toggleSection('behavior')}>
              <SectionHeaderLeft>
                <SectionIcon $variant="behavior">{'\u{1F9E0}'}</SectionIcon>
                <div>
                  <SectionTitle>Behavior</SectionTitle>
                  <SectionSummary>
                    {agent.config?.llmModel ?? 'GPT-4o'} {'\u00B7'} Temp{' '}
                    {agent.config?.temperature ?? 0.5} {'\u00B7'} &quot;
                    {(agent.config?.systemPrompt ?? '').slice(0, 40) ||
                      'Professional restaurant concierge'}
                    &quot;
                  </SectionSummary>
                </div>
              </SectionHeaderLeft>
              <SectionHeaderRight>
                <PendingBadge>2 changes</PendingBadge>
                <Chevron $open={activeSection === 'behavior'}>{'\u25B6'}</Chevron>
              </SectionHeaderRight>
            </SectionHeader>

            {activeSection === 'behavior' && (
              <SectionBody>
                <SubsectionLabel>Model &amp; Parameters</SubsectionLabel>
                <ConfigCard>
                  <ConfigRow>
                    <ConfigKey>LLM Provider</ConfigKey>
                    <ConfigValue>{agent.config?.llmProvider ?? 'OpenAI'}</ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigKey>Model</ConfigKey>
                    <ConfigValue>{agent.config?.llmModel ?? 'GPT-4o'}</ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigKey>Temperature</ConfigKey>
                    <ConfigValue $changed>
                      {agent.config?.temperature ?? 0.8}
                    </ConfigValue>
                  </ConfigRow>
                  <ConfigRow>
                    <ConfigKey>Max Tokens</ConfigKey>
                    <ConfigValue>{agent.config?.maxTokens ?? 2048}</ConfigValue>
                  </ConfigRow>
                </ConfigCard>

                <SubsectionLabel>System Prompt</SubsectionLabel>
                <PromptPreview>
                  {agent.config?.systemPrompt ||
                    `You are ${agentName}, a professional restaurant concierge.\nAddress all guests as "valued guest" and maintain a formal, courteous tone throughout all interactions.\n\nYou manage a team of specialized agents:\n- emily-menu: For menu-related questions\n- emily-reservations: For table bookings\n\nRoute guest queries to the appropriate specialist. If unsure, ask the guest for clarification before delegating.`}
                </PromptPreview>

                <SubsectionLabel>Conversation Style</SubsectionLabel>
                <StyleBadges>
                  {mockConversationStyle.map((style) => (
                    <Badge key={style} variant="primary" size="sm">
                      {style}
                    </Badge>
                  ))}
                </StyleBadges>
              </SectionBody>
            )}
          </SectionCard>

          {/* ===== SECTION 2: PROMPTS & OUTPUT ===== */}
          <SectionCard $active={activeSection === 'prompts'}>
            <SectionHeader onClick={() => toggleSection('prompts')}>
              <SectionHeaderLeft>
                <SectionIcon $variant="prompts">{'\u{1F4DD}'}</SectionIcon>
                <div>
                  <SectionTitle>Prompts &amp; Output</SectionTitle>
                  <SectionSummary>
                    System prompt {'\u00B7'} {mockSpecialistPrompts.length} specialist prompt
                    {mockSpecialistPrompts.length !== 1 ? 's' : ''} {'\u00B7'}{' '}
                    {mockOutputFormat.type} output
                  </SectionSummary>
                </div>
              </SectionHeaderLeft>
              <Chevron $open={activeSection === 'prompts'}>{'\u25B6'}</Chevron>
            </SectionHeader>

            {activeSection === 'prompts' && (
              <SectionBody>
                <SubsectionLabel>System Prompt</SubsectionLabel>
                <PromptFullPreview>
                  {agent.config?.systemPrompt ||
                    `You are ${agentName}, a professional restaurant concierge.\nAddress all guests as "valued guest" and maintain a formal, courteous tone throughout all interactions.\n\nYou manage a team of specialized agents:\n- emily-menu: For menu-related questions\n- emily-reservations: For table bookings\n\nRoute guest queries to the appropriate specialist. If unsure, ask the guest for clarification before delegating.`}
                </PromptFullPreview>

                {mockFleetConnections.length > 0 && mockSpecialistPrompts.length > 0 && (
                  <>
                    <SubsectionLabel style={{ marginTop: '1.25rem' }}>
                      {'\u{1F465}'} Specialist Prompts
                    </SubsectionLabel>
                    {mockSpecialistPrompts.map((sp) => (
                      <SpecialistPromptCollapsible
                        key={sp.agentName}
                        agentName={sp.displayName}
                        prompt={sp.prompt}
                      />
                    ))}
                  </>
                )}

                <SubsectionLabel style={{ marginTop: '1.25rem' }}>
                  {'\u{1F4C4}'} Output Format
                </SubsectionLabel>
                <ConfigCard>
                  <ConfigRow>
                    <ConfigKey>Format Type</ConfigKey>
                    <ConfigValue>{mockOutputFormat.type}</ConfigValue>
                  </ConfigRow>
                  {mockOutputFormat.schemaFile && (
                    <ConfigRow>
                      <ConfigKey>Schema File</ConfigKey>
                      <ConfigValue style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {mockOutputFormat.schemaFile}
                      </ConfigValue>
                    </ConfigRow>
                  )}
                </ConfigCard>

                {mockOutputFormat.schemaFields && mockOutputFormat.schemaFields.length > 0 && (
                  <>
                    <SubsectionLabel style={{ marginTop: '0.75rem' }}>
                      Response Schema
                    </SubsectionLabel>
                    <ConfigCard>
                      {mockOutputFormat.schemaFields.map((field) => (
                        <SchemaFieldRow key={field.name}>
                          <SchemaFieldName>{field.name}</SchemaFieldName>
                          <SchemaFieldType variant="primary" size="sm">
                            {field.type}
                          </SchemaFieldType>
                          {field.required && (
                            <Badge variant="warning" size="sm">
                              required
                            </Badge>
                          )}
                        </SchemaFieldRow>
                      ))}
                    </ConfigCard>
                  </>
                )}
              </SectionBody>
            )}
          </SectionCard>

          {/* ===== SECTION 3: KNOWLEDGE ===== */}
          <SectionCard $active={activeSection === 'knowledge'}>
            <SectionHeader onClick={() => toggleSection('knowledge')}>
              <SectionHeaderLeft>
                <SectionIcon $variant="knowledge">{'\u{1F4DA}'}</SectionIcon>
                <div>
                  <SectionTitle>Knowledge</SectionTitle>
                  <SectionSummary>
                    {mockDocuments.length} documents (
                    {mockDocuments.filter((d) => d.status === 'indexed').length} indexed) {'\u00B7'}{' '}
                    {mockKnowledgeSources.length} connected source
                    {mockKnowledgeSources.length !== 1 ? 's' : ''}
                  </SectionSummary>
                </div>
              </SectionHeaderLeft>
              <SectionHeaderRight>
                <PendingBadge>1 change</PendingBadge>
                <SectionActionBtn onClick={(e) => e.stopPropagation()}>
                  + Integrations
                </SectionActionBtn>
                <Chevron $open={activeSection === 'knowledge'}>{'\u25B6'}</Chevron>
              </SectionHeaderRight>
            </SectionHeader>

            {activeSection === 'knowledge' && (
              <SectionBody>
                <SubsectionLabel>{'\u{1F4C4}'} Uploaded Documents</SubsectionLabel>
                <ConfigCard>
                  {mockDocuments.map((doc) => (
                    <FileItem key={doc.id}>
                      <FileIcon>{doc.type === 'XLSX' ? '\u{1F4CA}' : '\u{1F4C4}'}</FileIcon>
                      <FileInfo>
                        <FileName>{doc.name}</FileName>
                        <FileMeta>
                          {doc.type} {'\u00B7'} {doc.size} {'\u00B7'} Uploaded {doc.uploadDate}
                        </FileMeta>
                      </FileInfo>
                      <Badge variant={FILE_STATUS_VARIANT[doc.status]} size="sm">
                        {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                      </Badge>
                    </FileItem>
                  ))}
                </ConfigCard>

                <UploadZone>
                  {'\u2601'} Drop files here or click to upload
                  <UploadZoneHint>PDF, DOCX, XLSX, CSV, TXT, images, JSON</UploadZoneHint>
                </UploadZone>

                <SubsectionLabel style={{ marginTop: '1.25rem' }}>
                  {'\u{1F517}'} Connected Knowledge Sources
                </SubsectionLabel>

                {mockKnowledgeSources.map((src) => (
                  <SourceCard key={src.id}>
                    <SourceIcon $bg="#6B4FBB22" $color="#6B4FBB">
                      {src.icon}
                    </SourceIcon>
                    <SourceInfo>
                      <SourceName>{src.name}</SourceName>
                      <SourceMeta>
                        {src.workspace} {'\u00B7'} {src.spaces?.length ?? 0} spaces {'\u00B7'}{' '}
                        {src.pageCount} pages {'\u00B7'} Synced {src.lastSynced}
                      </SourceMeta>
                    </SourceInfo>
                    <StatusDot $status={src.status} />
                  </SourceCard>
                ))}

                <IntegrationLink>
                  {'\u{1F517}'} Import Knowledge from Integrations {'\u2197'}
                </IntegrationLink>
              </SectionBody>
            )}
          </SectionCard>

          {/* ===== SECTION 4: TASKS & TOOLS ===== */}
          <SectionCard $active={activeSection === 'tasks'}>
            <SectionHeader onClick={() => toggleSection('tasks')}>
              <SectionHeaderLeft>
                <SectionIcon $variant="tasks">{'\u26A1'}</SectionIcon>
                <div>
                  <SectionTitle>Tasks &amp; Tools</SectionTitle>
                  <SectionSummary>
                    {mockTasks.length} tasks {'\u00B7'} {mockTools.length} tool
                    {mockTools.length !== 1 ? 's' : ''} {'\u00B7'} Delegation enabled (
                    {mockFleetConnections.length} sub-agents)
                  </SectionSummary>
                </div>
              </SectionHeaderLeft>
              <SectionHeaderRight>
                <SectionActionBtn onClick={(e) => e.stopPropagation()}>
                  + Add Task
                </SectionActionBtn>
                <Chevron $open={activeSection === 'tasks'}>{'\u25B6'}</Chevron>
              </SectionHeaderRight>
            </SectionHeader>

            {activeSection === 'tasks' && (
              <SectionBody>
                <SubsectionLabel>{'\u{1F4CB}'} Task Definitions</SubsectionLabel>
                <ConfigCard>
                  {mockTasks.map((task) => (
                    <TaskItem key={task.id}>
                      <TaskIconWrap>{task.icon}</TaskIconWrap>
                      <TaskInfo>
                        <TaskName>{task.name}</TaskName>
                        <TaskDesc>{task.description}</TaskDesc>
                        <SchemaPills>
                          {task.inputs.map((f) => (
                            <SchemaPill key={`in-${f.name}`}>
                              in: {f.name}
                              {f.required === false ? '?' : ''}: {f.type}
                            </SchemaPill>
                          ))}
                          {task.outputs.map((f) => (
                            <SchemaPill key={`out-${f.name}`}>
                              out: {f.name}: {f.type}
                            </SchemaPill>
                          ))}
                        </SchemaPills>
                      </TaskInfo>
                    </TaskItem>
                  ))}
                </ConfigCard>

                <SubsectionLabel style={{ marginTop: '1.25rem' }}>
                  {'\u{1F527}'} Connected Tools
                </SubsectionLabel>
                {mockTools.map((tool) => (
                  <SourceCard key={tool.id}>
                    <SourceIcon $bg="var(--accent-subtle)" $color="var(--accent)">
                      {tool.icon}
                    </SourceIcon>
                    <SourceInfo>
                      <SourceName>{tool.name}</SourceName>
                      <SourceMeta>
                        Used by {tool.usedBy.join(', ')} {'\u00B7'}{' '}
                        {tool.status.charAt(0).toUpperCase() + tool.status.slice(1)}
                      </SourceMeta>
                    </SourceInfo>
                    <StatusDot $status={tool.status} />
                  </SourceCard>
                ))}

                <SubsectionLabel style={{ marginTop: '1.25rem' }}>
                  {'\u2699'} Capabilities
                </SubsectionLabel>
                <ConfigCard>
                  {capabilities.map((cap) => (
                    <CapabilityRow key={cap.id}>
                      <CapabilityLabel>{cap.label}</CapabilityLabel>
                      <Toggle
                        value={cap.enabled}
                        onChange={() => toggleCapability(cap.id)}
                        size="sm"
                      />
                    </CapabilityRow>
                  ))}
                </ConfigCard>

                <SubsectionLabel style={{ marginTop: '1.25rem' }}>
                  {'\u{1F465}'} Fleet Connections (Delegation)
                </SubsectionLabel>
                <ConfigCard>
                  {mockFleetConnections.map((fc) => (
                    <FleetMember key={fc.id}>
                      <FleetIcon $color={fc.color}>{fc.initials}</FleetIcon>
                      <div>
                        <FleetName>{fc.name}</FleetName>
                        <FleetDesc>
                          {fc.type} {'\u00B7'} {fc.description}
                        </FleetDesc>
                      </div>
                    </FleetMember>
                  ))}
                </ConfigCard>
                <DashedBtn>+ Add Sub-Agent</DashedBtn>
              </SectionBody>
            )}
          </SectionCard>
        </PanelScroll>

        {/* ===== VERSION BAR ===== */}
        <VersionBarWrap>
          <VersionLabel>Version:</VersionLabel>
          <VersionBadge $variant="draft">
            {draftVersion?.version ?? 'Draft'}
          </VersionBadge>
          <VersionArrow>{'\u2192'}</VersionArrow>
          <VersionBadge $variant="sandbox">
            {sandboxVersion?.version ?? 'v1.3-beta'} Sandbox
          </VersionBadge>
          <VersionArrow>{'\u2192'}</VersionArrow>
          <VersionBadge $variant="published">
            {publishedVersion?.version ?? 'v1.2'} Published
          </VersionBadge>
        </VersionBarWrap>
      </PanelRight>
    </TwoPanel>
  );
};

ConfigureTab.displayName = 'ConfigureTab';
